import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaFileAlt,
  FaFilter,
  FaHome,
  FaInfoCircle,
  FaPlus,
  FaPrint,
  FaSearch,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

//Created By Cursor
const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const BRAND = {
  navy: "#0f172a",
  bg: "#f5f7fb",
};

const STATUS_STYLES = {
  draft: { bg: "#EEF1F8", fg: "#475569", label: "Draft" },
  submitted: { bg: "#FFF6E9", fg: "#B5750B", label: "Submitted" },
  locked: { bg: "#E6F8F6", fg: "#0F8A7E", label: "Locked" },
  approved: { bg: "#E6F8F6", fg: "#0F8A7E", label: "Approved" },
};

//Created By Cursor
function subjectsForClass(classId, rows = []) {
  const seen = new Map();
  rows
    .filter((r) => String(r.class_id) === String(classId))
    .forEach((r) => {
      if (!seen.has(String(r.subject_id))) seen.set(String(r.subject_id), r);
    });
  return Array.from(seen.values());
}

//Created By Cursor
function subjectLabel(row) {
  return row?.subject_name || row?.book_name || row?.subject_id || "Subject";
}

//Created By Cursor
function Pill({ bg, fg, children }) {
  return (
    <span className="qb-pill" style={{ background: bg, color: fg }}>
      {children}
    </span>
  );
}

//Created By Cursor
function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

//Created By Cursor
export default function QuestionPapers() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [papers, setPapers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    setSessionId(current?.session_id || "");
  }, []);

  //Created By Cursor
  const loadLookups = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    try {
      const [classRes, subjectRes] = await Promise.all([
        axios.get(`${API_BASE()}getQuestionBankClasses.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId || undefined,
          },
        }),
        axios.get(`${API_BASE()}getQuestionBankClassSubjects.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId || undefined,
          },
        }),
      ]);
      setClasses(
        classRes.data?.api_status === "OK" && Array.isArray(classRes.data.data)
          ? classRes.data.data
          : []
      );
      setClassSubjects(
        subjectRes.data?.api_status === "OK" &&
          Array.isArray(subjectRes.data.data)
          ? subjectRes.data.data
          : []
      );
    } catch (err) {
      console.error("[QuestionPapers] lookup error:", err);
    }
  }, [sessionId]);

  //Created By Cursor
  const loadPapers = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) return;
      if (soft) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await axios.get(`${API_BASE()}getQuestionPapers.php`, {
          params: {
            school_code: schoolCode,
            class_id: classFilter === "all" ? undefined : classFilter,
            subject_id: subjectFilter === "all" ? undefined : subjectFilter,
            status: statusFilter === "all" ? undefined : statusFilter,
            search: search.trim() || undefined,
          },
        });
        if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
          setPapers(res.data.data);
        } else {
          setPapers([]);
          if (res.data?.api_remark) {
            myToast.notify_warning(res.data.api_remark);
          }
        }
      } catch (err) {
        console.error("[QuestionPapers] list error:", err);
        setPapers([]);
        myToast.notify_warning("Could not load question papers.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classFilter, subjectFilter, statusFilter, search]
  );

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    const t = setTimeout(() => loadPapers(), 250);
    return () => clearTimeout(t);
  }, [loadPapers]);

  const subjectOptions = useMemo(
    () =>
      classFilter === "all"
        ? []
        : subjectsForClass(classFilter, classSubjects),
    [classFilter, classSubjects]
  );

  useEffect(() => {
    if (
      subjectFilter !== "all" &&
      !subjectOptions.some(
        (s) => String(s.subject_id) === String(subjectFilter)
      )
    ) {
      setSubjectFilter("all");
    }
  }, [subjectOptions, subjectFilter]);

  const classNameById = useMemo(() => {
    const map = new Map();
    classes.forEach((c) => map.set(String(c.class_id), c.class_name));
    return map;
  }, [classes]);

  const subjectNameById = useMemo(() => {
    const map = new Map();
    classSubjects.forEach((s) => {
      if (!map.has(String(s.subject_id))) {
        map.set(String(s.subject_id), subjectLabel(s));
      }
    });
    return map;
  }, [classSubjects]);

  //Created By Cursor
  const handleDelete = useCallback(
    async (paper) => {
      const schoolCode = SessionManager.getSchoolCode();
      const id = paper?.id;
      if (!schoolCode || !id) return;
      const label = paper.exam_name || "this paper";
      if (
        !window.confirm(
          `Delete "${label}" permanently?\n\nThis removes the paper, all sections, and question slots. This cannot be undone.`
        )
      ) {
        return;
      }
      setDeletingId(String(id));
      try {
        const body = new URLSearchParams();
        body.set("school_code", schoolCode);
        body.set("id", String(id));
        const res = await axios.post(
          `${API_BASE()}deleteQuestionPaper.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        if (res.data?.api_status === "OK") {
          myToast.notify_success(
            res.data?.message || "Question paper deleted permanently."
          );
          await loadPapers({ soft: true });
        } else {
          myToast.notify_warning(
            res.data?.api_remark ||
              res.data?.message ||
              "Failed to delete question paper."
          );
        }
      } catch (err) {
        console.error("[QuestionPapers] delete error:", err);
        myToast.notify_warning(
          err?.response?.data?.api_remark ||
            err?.response?.data?.message ||
            "Failed to delete question paper."
        );
      } finally {
        setDeletingId(null);
      }
    },
    [loadPapers]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  const busy = loading || refreshing;

  return (
    <div
      className="content payroll-page sm-page qb-page pb-page"
      style={{ background: BRAND.bg }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="qb-crumb-link"
                onClick={() => history.push("/AdminMain/Academic/1")}
              >
                Academic
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Question Paper</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaFileAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Question Paper</h1>
              <p className="sm-hero__sub">
                {papers.length} paper{papers.length === 1 ? "" : "s"} · draft to
                export
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              onClick={() => loadPapers({ soft: true })}
              disabled={busy}
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="qb-hero-btn"
              onClick={() => history.push("/AdminMain/PaperBuilder")}
            >
              <FaPlus /> New Question paper
            </button>
          </div>
        </header>

        <div className="qb-content">
          <div className="qb-filters">
            <label className="qb-search">
              <FaSearch aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search exam name…"
                aria-label="Search papers"
              />
            </label>
            <div className="qb-filter-row">
              <FaFilter className="qb-filter-icon" aria-hidden />
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                aria-label="Class filter"
              >
                <option value="all">All Classes</option>
                {classes.map((c) => (
                  <option key={c.class_id} value={c.class_id}>
                    {c.class_name}
                  </option>
                ))}
              </select>
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                aria-label="Subject filter"
                disabled={classFilter === "all"}
              >
                <option value="all">All Subjects</option>
                {subjectOptions.map((s) => (
                  <option key={s.subject_id} value={s.subject_id}>
                    {subjectLabel(s)}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Status filter"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="locked">Locked</option>
              </select>
            </div>
          </div>

          {loading && !papers.length ? (
            <div className="qb-empty">
              <MyLoader />
            </div>
          ) : papers.length === 0 ? (
            <div className="qb-empty">
              No question papers yet. Create one with New Question paper.
            </div>
          ) : (
            <div className="qb-list">
              {papers.map((p) => {
                const statusKey = String(p.status || "").toLowerCase();
                const st =
                  STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
                const canPrint =
                  statusKey === "locked" || statusKey === "approved";
                const filled = Number(p.questions_filled || 0);
                const slots = Number(p.question_slots || 0);
                return (
                  <article key={p.id} className="qb-card">
                    <div className="qb-card__body">
                      <p className="qb-card__text">{p.exam_name}</p>
                      <div className="qb-card__meta">
                        <Pill bg="#EEF1F8" fg={BRAND.navy}>
                          {classNameById.get(String(p.class_id)) || p.class_id}
                        </Pill>
                        <Pill bg="#EEF1F8" fg={BRAND.navy}>
                          {subjectNameById.get(String(p.subject_id)) ||
                            p.subject_id}
                        </Pill>
                        <Pill bg={st.bg} fg={st.fg}>
                          {st.label}
                        </Pill>
                        <Pill bg="#EAF9F7" fg="#0F8A7E">
                          {p.max_marks} marks
                        </Pill>
                        <Pill bg="#F2F2F2" fg="#666">
                          {p.section_count || 0} sections
                        </Pill>
                        <Pill bg="#F2F2F2" fg="#666">
                          {filled}/{slots} filled
                        </Pill>
                        {p.exam_date ? (
                          <Pill bg="#FFF6E9" fg="#B5750B">
                            {formatDate(p.exam_date)}
                          </Pill>
                        ) : null}
                        {p.date_created ? (
                          <span className="qb-scanned">
                            Created {formatDate(p.date_created)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="qb-card__actions">
                      {canPrint ? (
                        <button
                          type="button"
                          className="qb-details-btn"
                          title="Print question paper"
                          onClick={() =>
                            history.push(
                              `/AdminMain/PrintQuestionPaper/${encodeURIComponent(
                                p.id
                              )}`
                            )
                          }
                        >
                          <FaPrint aria-hidden />
                          <span>Print</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="qb-details-btn"
                        title="Open paper"
                        onClick={() =>
                          history.push(`/AdminMain/PaperBuilder/${p.id}`)
                        }
                      >
                        <FaInfoCircle aria-hidden />
                        <span>Details</span>
                      </button>
                      <button
                        type="button"
                        className="qb-icon-btn qb-icon-btn--danger"
                        title="Delete permanently"
                        disabled={deletingId === String(p.id) || busy}
                        onClick={() => handleDelete(p)}
                      >
                        <FaTrash aria-hidden />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
